import type { DeepReadonly, Ref } from 'vue'
import makeEscapedRegExp from '@/lib/make-escaped-reg-exp'

type UseObjectSearch<T> = {
  search: Ref<string>
  setSearch: (search: string) => void
  results: Ref<DeepReadonly<T[]>>
}

export function useObjectSearch<T extends { id: any }>({
  objects,
  searchProperty,
  compFn = (p, n) => p.id === n.id,
}: {
  objects: Ref<DeepReadonly<T[]>>
  searchProperty: keyof DeepReadonly<T>
  compFn?: (prev: DeepReadonly<T>, next: DeepReadonly<T>) => boolean
}): UseObjectSearch<T> {
  const search = ref('')
  const results: Ref<T[]> = ref([])

  function _compareLists(a: DeepReadonly<T[]>, b: DeepReadonly<T[]>) {
    if (a.length !== b.length) return false
    return a.every((aElem, i) => compFn(aElem, b[i]))
  }

  function _handleSearchResults(objects: DeepReadonly<T[]>, search: string) {
    if (!search) {
      results.value = objects as any[]
      return
    }
    const searchFilters = search.split(' ').map(makeEscapedRegExp)
    results.value = objects.filter(obj => searchFilters.every(x => x.test(obj[searchProperty] as string))) as T[]
  }

  watch([search, objects], ([search, objects], [oldSearch, oldObjects]) => {
    // escape early if equivalent
    if (search === oldSearch && _compareLists(objects, oldObjects)) return

    _handleSearchResults(objects, search)
  })

  _handleSearchResults(objects.value, '')

  return {
    search: readonly(search),
    setSearch: (s: string) => (search.value = s),
    results: readonly(results),
  }
}
